import type { DropdownOption } from '@/features/common/types/DropdownOption';
import {
  cleanFilters,
  ISO_DAY,
  orderPeriod,
  withFilters,
} from '@/features/common/listingFilters';
import { isStoryType } from '@/features/publicsite/news/storyType';
import type { NewsFilterName } from '@/features/publicsite/types/NewsFilterName';

const FILTER_NAMES: NewsFilterName[] = ['q', 'category', 'type', 'from', 'to'];

function acceptable(name: NewsFilterName, value: string): boolean {
  if (name === 'type') return isStoryType(value);
  if (name === 'from' || name === 'to') return ISO_DAY.test(value);
  return true;
}

/**
 * The filters the URL currently holds, cleaned: trimmed, blanks left out, `type` only when it is
 * one of the two story types, period ends only when they are ISO days — and a period given
 * backwards turned the right way round, as the server reads it. Every other function here reads
 * the URL through this, so the request, the page links, the period's label and the reset button can
 * never disagree about what is being filtered.
 */
export function newsFilters(params: URLSearchParams): Partial<Record<NewsFilterName, string>> {
  return orderPeriod(cleanFilters(params, FILTER_NAMES, acceptable));
}

/**
 * The URL after a change to some of the filters, with no `page` — a filter change starts the list
 * again from its first page, since page 3 of the old list is not a place in the new one.
 */
export function withNewsFilters(
  params: URLSearchParams,
  changes: Partial<Record<NewsFilterName, string | null>>,
): URLSearchParams {
  return withFilters(params, changes, newsFilters);
}

/**
 * The category list's choices: "all" first, then every category the archive has — and the one the
 * URL filters by, if it is not among them.
 *
 * A link can name a category that no longer has a published story, or arrive before (or without)
 * the category list: the list is narrowed all the same, and a control that then showed "Toate
 * categoriile" said the opposite of what the page was doing, with no way to see the filter in
 * order to take it off.
 *
 * Each choice reads as `labelOf` names it — the page's language, see categoryLabel — and is worth
 * the stored text: that is what ?category= and the request to the server carry, as before.
 */
export function categoryFilterOptions(
  categories: string[],
  current: string | undefined,
  allLabel: string,
  labelOf: (stored: string) => string = (stored) => stored,
): DropdownOption[] {
  const names = current && !categories.includes(current) ? [...categories, current] : categories;
  return [{ value: '', label: allLabel }, ...names.map((c) => ({ value: c, label: labelOf(c) }))];
}

/** How many filters are narrowing the list — what decides the "Resetează" button and the count. */
export function activeNewsFilterCount(params: URLSearchParams): number {
  return Object.keys(newsFilters(params)).length;
}

/**
 * The address of another page of the same filtered list.
 *
 * Carries the filters across, so paging a narrowed list does not quietly widen it again; page zero
 * is written without `page=` so the first page has one address rather than two. Unfiltered, that is
 * a bare /stiri, exactly the address the page had before it had filters.
 */
export function newsPageHref(params: URLSearchParams, page: number): string {
  const next = new URLSearchParams(newsFilters(params));
  if (page > 0) next.set('page', String(page));
  const qs = next.toString();
  return qs ? `/stiri?${qs}` : '/stiri';
}
